import React from 'react';
import { ArrowLeft } from 'lucide-react';
import { Button } from './components/ui/actions/Button.jsx';
import { Card } from './components/ui/display/Card.jsx';
import { Stepper } from './components/widget/Stepper.jsx';
import { PriceSummary, buildSummaryLines } from './components/widget/PriceSummary.jsx';
import { StepService } from './steps/StepService.jsx';
import { StepStaff } from './steps/StepStaff.jsx';
import { StepSlot } from './steps/StepSlot.jsx';
import { StepExtras } from './steps/StepExtras.jsx';
import { StepDetails } from './steps/StepDetails.jsx';
import { StepOmisePay } from './steps/StepOmisePay.jsx';
import { StepDone } from './steps/StepDone.jsx';
import { api, APIError } from './api.js';
import { detectLocale, pickLocalized, tFor } from './i18n/i18n.js';
import { todayIso, plusDaysIso } from './lib/price.js';
import { readSavedReservation, saveReservation, clearSavedReservation } from './lib/reservationStorage.js';
import {
  buildCheckoutHandoffUrl,
  CHECKOUT_MESSAGE_TYPE,
  consumeCheckoutHandoff,
  isEmbeddedMode,
} from './lib/embedHandoff.js';
import { CountdownTimer } from './components/widget/CountdownTimer.jsx';
import { LocaleSwitch } from './components/widget/LocaleSwitch.jsx';
import { Footer } from './components/widget/Footer.jsx';
import { PolicyDialog } from './components/widget/PolicyDialog.jsx';

// BookingWidget — the top-level orchestrator. Owns all booking draft
// state and runs the customer through 5 steps: service → time → details
// → pay → done. Staff is a sub-step inside the time step whenever the shop
// has eligible staff, so customers can see who they are booking with.
//
// Each step is dumb: receives props, calls `onX(...)` callbacks. The
// orchestrator owns:
//   - service / staff / slot / extras selection
//   - server fetches for services, staff, schedule
//   - booking create → payment intent → success
//
// On mount, the orchestrator:
//   1. Reads the slug from window.location.pathname (last segment).
//   2. Hydrates the SSR-injected shop identity (HTMLRewriter pre-rendered
//      name, address, phone, etc. into <html data-shop-name=…> attrs).
//   3. Picks locale from the browser language or the merchant default.

const initialUiState = {
  loading: true,
  error: null,
  shop: null,
  services: [],
  staff: [],
  categories: [],
  schedule: { days: [] },
};

export function BookingWidget() {
  const slug = readSlugFromUrl();
  const embedded = isEmbeddedMode();
  const [ui, setUi] = React.useState(initialUiState);
  const [scheduleLoading, setScheduleLoading] = React.useState(false);
  const [loadedScheduleKey, setLoadedScheduleKey] = React.useState(null);
  const scheduleRequestRef = React.useRef(0);
  const idempotencyKeyRef = React.useRef(null);
  if (!idempotencyKeyRef.current) idempotencyKeyRef.current = crypto.randomUUID();
  const [step, setStep] = React.useState(1);
  const [draft, setDraft] = React.useState({
    serviceId: null,
    staffId: null,
    slotDate: null,
    slot: null,
    extrasQuantities: {},
  });
  const [booking, setBooking] = React.useState(null);
  const [checkoutHandoff] = React.useState(() => consumeCheckoutHandoff(slug));
  const [savedReservation, setSavedReservation] = React.useState(() =>
    checkoutHandoff || readSavedReservation(slug)
  );
  const [locale, setLocale] = React.useState('en');
  const [payment, setPayment] = React.useState({
    bookingId: null,
    reservationToken: null,
    paymentProvider: null,
    publicKey: null,
    supportedMethods: [],
    nextAction: null,
    expiresAt: null,
    totalPrice: null,
    currency: 'THB',
    state: 'idle',
    submitting: false,
    error: null,
  });

  const t = tFor(locale);

  // Dialog state for the policy / contact modals. null = closed.
  const [dialog, setDialog] = React.useState(null);

  // Bootstrap. Fetches shop, services, staff in parallel; derives locale.
  // Locale priority: stored user pick > shop default + browser languages.
  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [profile, services, staff, schedule] = await Promise.all([
          api.profile(slug),
          api.services(slug),
          api.staff(slug),
          api.schedule(slug, { from: todayIso(), to: plusDaysIso(13) }),
        ]);
        if (cancelled) return;
        const shop = profile.shop;
        setUi({
          loading: false,
          error: null,
          shop,
          services: services.services || [],
          staff: staff.staff || [],
          categories: collectCategories(services.services || []),
          schedule: schedule || { days: [] },
        });
        setLoadedScheduleKey(scheduleKey(null, null));
        const stored = readStoredLocale(slug);
        setLocale(stored || detectLocale(shop.localeDefault || 'en', navigator.languages));
        // Mark SPA hydrated so the SEO block can be hidden.
        document.documentElement.dataset.spaHydrated = 'true';
      } catch (err) {
        if (cancelled) return;
        setUi((u) => ({ ...u, loading: false, error: err.message || 'Error' }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  React.useEffect(() => {
    if (checkoutHandoff) return;
    setSavedReservation(readSavedReservation(slug));
  }, [checkoutHandoff, slug]);

  const storeReservation = React.useCallback((reservation) => {
    saveReservation(slug, reservation);
    setSavedReservation(reservation);
  }, [slug]);

  const showConfirmedReservation = React.useCallback((saved, serverBooking) => {
    const confirmed = {
      ...saved,
      ...serverBooking,
      id: serverBooking.id,
      bookingId: serverBooking.id,
      status: serverBooking.status,
      serviceName:
        pickLocalized(serverBooking.serviceName, locale) || saved.serviceName,
    };
    storeReservation(confirmed);
    setBooking(confirmed);
    setPayment((current) => ({ ...current, state: 'confirmed', error: null }));
    setStep(5);
  }, [locale, storeReservation]);

  const refreshReservation = React.useCallback(async (saved, { resume = false } = {}) => {
    if (!saved?.reservationToken) {
      if (saved?.bookingCode) {
        setBooking({ ...saved, status: saved.status || 'confirmed' });
        setStep(5);
      }
      return null;
    }
    try {
      const result = await api.booking(slug, saved.id, saved.reservationToken);
      const serverBooking = result?.booking;
      if (!serverBooking) return null;
      if (serverBooking.status === 'confirmed' || serverBooking.status === 'completed') {
        showConfirmedReservation(saved, serverBooking);
        return serverBooking;
      }
      const recovered = {
        ...saved,
        ...serverBooking,
        bookingId: serverBooking.id,
        serviceName:
          pickLocalized(serverBooking.serviceName, locale) || saved.serviceName,
      };
      storeReservation(recovered);
      setBooking(recovered);
      if (serverBooking.status === 'cancelled') {
        setPayment((current) => ({ ...current, state: 'expired' }));
        setStep(1);
        return serverBooking;
      }
      if (serverBooking.paymentStatus === 'processing') {
        let providerContext = null;
        if (recovered.paymentProvider === 'omise') {
          providerContext = await api.resumePayment(slug, recovered.id, recovered.reservationToken);
          if (providerContext.status === 'confirmed') {
            const confirmedResult = await api.booking(slug, recovered.id, recovered.reservationToken);
            if (confirmedResult?.booking) {
              showConfirmedReservation(recovered, confirmedResult.booking);
              return confirmedResult.booking;
            }
          }
        }
        const refreshedReservation = providerContext
          ? { ...recovered, paymentStatus: providerContext.paymentStatus }
          : recovered;
        if (providerContext) {
          storeReservation(refreshedReservation);
          setBooking(refreshedReservation);
        }
        setPayment((current) => ({
          ...current,
          bookingId: recovered.id,
          reservationToken: recovered.reservationToken,
          paymentProvider: providerContext?.paymentProvider || recovered.paymentProvider || current.paymentProvider,
          publicKey: providerContext?.publicKey || current.publicKey,
          supportedMethods: providerContext?.supportedMethods || current.supportedMethods,
          nextAction: providerContext ? (providerContext.nextAction ?? null) : current.nextAction,
          expiresAt: recovered.expiresAt,
          totalPrice: recovered.totalPrice,
          currency: recovered.currency,
          state: providerContext && providerContext.paymentStatus !== 'processing'
            ? 'ready'
            : (current.state === 'confirming' ? 'confirming' : 'processing'),
          error: null,
        }));
        setStep(4);
        return serverBooking;
      }
      if (resume && serverBooking.status === 'pending') {
        const resumed = await api.resumePayment(slug, recovered.id, recovered.reservationToken);
        if (resumed.status === 'confirmed') {
          const confirmedResult = await api.booking(slug, recovered.id, recovered.reservationToken);
          if (confirmedResult?.booking) {
            showConfirmedReservation(recovered, confirmedResult.booking);
            return confirmedResult.booking;
          }
        }
        setPayment((current) => ({
          bookingId: recovered.id,
          reservationToken: recovered.reservationToken,
          paymentProvider: resumed.paymentProvider,
          publicKey: resumed.publicKey,
          supportedMethods: resumed.supportedMethods || [],
          nextAction: resumed.nextAction || null,
          expiresAt: resumed.expiresAt,
          totalPrice: resumed.totalPrice,
          currency: resumed.currency,
          state: resumed.paymentStatus === 'processing'
            ? (current.state === 'confirming' ? 'confirming' : 'processing')
            : 'ready',
          submitting: false,
          error: null,
        }));
        setStep(4);
      }
      return serverBooking;
    } catch (error) {
      if (
        error instanceof APIError &&
        error.status === 410 &&
        error.message === 'BOOKING_EXPIRED'
      ) {
        const expired = { ...saved, status: 'cancelled', paymentStatus: 'failed' };
        storeReservation(expired);
        setPayment((current) => ({ ...current, state: 'expired' }));
        setStep(1);
        return expired;
      }
      throw error;
    }
  }, [locale, showConfirmedReservation, slug, storeReservation]);

  const handoffStartedRef = React.useRef(false);
  React.useEffect(() => {
    if (ui.loading || !checkoutHandoff || handoffStartedRef.current) return;
    handoffStartedRef.current = true;
    storeReservation(checkoutHandoff);
    setBooking(checkoutHandoff);
    setPayment((current) => ({ ...current, state: 'confirming', error: null }));
    setStep(4);
    refreshReservation(checkoutHandoff, { resume: true }).catch((error) => {
      setPayment((current) => ({
        ...current,
        state: 'idle',
        error: mapBookingError(error, t),
      }));
      setStep(1);
    });
  }, [checkoutHandoff, refreshReservation, storeReservation, t, ui.loading]);

  React.useEffect(() => {
    if (ui.loading || !savedReservation?.reservationToken) return;
    const params = new URLSearchParams(window.location.search);
    if (
      params.get('step') !== 'payment-return' ||
      params.get('booking') !== String(savedReservation.id)
    ) {
      if (params.get('step') === 'payment-return') {
        window.history.replaceState({}, '', window.location.pathname);
      }
      return;
    }
    setPayment((current) => ({ ...current, state: 'confirming', error: null }));
    setBooking(savedReservation);
    setStep(4);
    refreshReservation(savedReservation, { resume: true }).catch(() => {
      setPayment((current) => ({ ...current, state: 'processing' }));
    });
    window.history.replaceState({}, '', window.location.pathname);
  }, [refreshReservation, savedReservation, ui.loading]);

  React.useEffect(() => {
    if (!savedReservation?.reservationToken || !['confirming', 'processing'].includes(payment.state)) return;
    const timer = window.setInterval(() => {
      refreshReservation(savedReservation, { resume: true }).catch(() => {});
    }, 2000);
    return () => window.clearInterval(timer);
  }, [payment.state, refreshReservation, savedReservation]);

  React.useEffect(() => {
    if (
      ui.loading ||
      !savedReservation?.reservationToken ||
      savedReservation.paymentStatus !== 'processing' ||
      payment.state !== 'idle'
    ) return;
    setBooking(savedReservation);
    setPayment((current) => ({
      ...current,
      bookingId: savedReservation.id,
      reservationToken: savedReservation.reservationToken,
      expiresAt: savedReservation.expiresAt,
      totalPrice: savedReservation.totalPrice,
      currency: savedReservation.currency,
      state: 'processing',
    }));
    setStep(4);
    refreshReservation(savedReservation, { resume: true }).catch(() => {});
  }, [payment.state, refreshReservation, savedReservation, ui.loading]);

  // Whenever the picked service or staff changes, refresh the schedule.
  // Cancellable so a fast picker doesn't race the previous fetch. The
  // server subdivides each template window into 30-min-aligned slot
  // starts that fit the service's durationMinutes.
  const refreshSchedule = React.useCallback(
    async (serviceId, staffId) => {
      const key = scheduleKey(serviceId, staffId);
      const requestId = ++scheduleRequestRef.current;
      setScheduleLoading(true);
      try {
        const res = await api.schedule(slug, {
          from: todayIso(),
          to: plusDaysIso(13),
          ...(serviceId ? { serviceId } : {}),
          ...(staffId ? { staffId } : {}),
        });
        if (requestId !== scheduleRequestRef.current) return;
        setUi((u) => ({ ...u, schedule: res || { days: [] } }));
        setLoadedScheduleKey(key);
        // Service selection clears slotDate. Once the service-specific
        // calendar arrives, land on the first date that can actually be
        // booked instead of making the customer choose a date before they
        // can see any times. The identity check also prevents a slow,
        // superseded request from changing the current draft.
        const firstAvailableDay = (res?.days || []).find(
          (day) => Array.isArray(day.slots) && day.slots.length > 0
        );
        if (firstAvailableDay) {
          setDraft((current) => {
            if (
              current.serviceId !== serviceId ||
              current.staffId !== staffId ||
              current.slotDate
            ) {
              return current;
            }
            return { ...current, slotDate: firstAvailableDay.date };
          });
        }
      } catch {
        if (requestId !== scheduleRequestRef.current) return;
        // Do not leave the previous therapist's schedule visible if the
        // replacement request fails.
        setUi((u) => ({ ...u, schedule: { days: [] } }));
        setLoadedScheduleKey(key);
      } finally {
        if (requestId === scheduleRequestRef.current) {
          setScheduleLoading(false);
        }
      }
    },
    [slug]
  );

  React.useEffect(() => {
    if (!draft.serviceId) return;
    refreshSchedule(draft.serviceId, draft.staffId);
  }, [draft.serviceId, draft.staffId, refreshSchedule]);

  const changeLocale = React.useCallback(
    (next) => {
      if (next !== 'en' && next !== 'th') return;
      writeStoredLocale(slug, next);
      setLocale(next);
    },
    [slug]
  );

  const cancelAndChooseAnother = React.useCallback(async () => {
    const active = savedReservation || booking;
    if (active?.reservationToken) {
      setPayment((current) => ({ ...current, submitting: true, error: null }));
      try {
        const result = await api.cancelUnpaidPayment(slug, active.id, active.reservationToken);
        if (result.status === 'confirmed') {
          await refreshReservation(active);
          return;
        }
        if (result.paymentStatus === 'processing') {
          storeReservation({ ...active, paymentStatus: 'processing' });
          setPayment((current) => ({
            ...current,
            state: 'processing',
            submitting: false,
            error: t('pay.cancelProcessing'),
          }));
          return;
        }
      } catch (error) {
        if (!(error instanceof APIError && error.status === 410)) {
          setPayment((current) => ({
            ...current,
            submitting: false,
            error: mapBookingError(error, t),
          }));
          return;
        }
      }
    }
    clearSavedReservation(slug);
    setSavedReservation(null);
    setBooking(null);
    setPayment({
      bookingId: null,
      reservationToken: null,
      paymentProvider: null,
      publicKey: null,
      supportedMethods: [],
      nextAction: null,
      expiresAt: null,
      totalPrice: null,
      currency: 'THB',
      state: 'idle',
      submitting: false,
      error: null,
    });
    idempotencyKeyRef.current = crypto.randomUUID();
    setStep(2);
    refreshSchedule(draft.serviceId, draft.staffId);
  }, [booking, draft.serviceId, draft.staffId, refreshReservation, refreshSchedule, savedReservation, slug, storeReservation, t]);

  const expireCurrentReservation = React.useCallback(async () => {
    const active = savedReservation || booking;
    if (!active?.reservationToken) return;
    try {
      const result = await api.cancelUnpaidPayment(slug, active.id, active.reservationToken);
      if (result.status === 'confirmed') {
        await refreshReservation(active);
        return;
      }
      if (result.paymentStatus === 'processing') {
        storeReservation({ ...active, paymentStatus: 'processing' });
        setPayment((current) => ({ ...current, state: 'processing' }));
        return;
      }
    } catch {
      // The server sweep/status endpoint will finish expiry. The local UI
      // still removes the now-invalid payment action immediately.
    }
    const expired = { ...active, status: 'cancelled', paymentStatus: 'failed' };
    storeReservation(expired);
    setPayment((current) => ({ ...current, state: 'expired' }));
  }, [booking, refreshReservation, savedReservation, slug, storeReservation]);

  const submitOmiseNonce = React.useCallback(async (submission) => {
    const active = booking || savedReservation;
    if (!active?.reservationToken) return;
    setPayment((current) => ({ ...current, submitting: true, error: null }));
    try {
      const result = await api.submitOmisePayment(
        slug,
        active.id,
        active.reservationToken,
        submission
      );
      storeReservation({
        ...active,
        paymentProvider: 'omise',
        paymentStatus: result.paymentStatus,
      });
      if (result.status === 'confirmed') {
        await refreshReservation(active);
        return;
      }
      setPayment((current) => ({
        ...current,
        paymentProvider: 'omise',
        publicKey: result.publicKey || current.publicKey,
        supportedMethods: result.supportedMethods || current.supportedMethods,
        nextAction: result.nextAction || null,
        expiresAt: result.expiresAt,
        state: result.paymentStatus === 'processing' ? 'processing' : 'ready',
        submitting: false,
        error: null,
      }));
      if (result.nextAction?.type === 'redirect' && result.nextAction.url) {
        window.location.assign(result.nextAction.url);
      }
    } catch (error) {
      setPayment((current) => ({
        ...current,
        state: 'ready',
        submitting: false,
        error: mapBookingError(error, t),
      }));
    }
  }, [booking, refreshReservation, savedReservation, slug, storeReservation, t]);

  const reloadPaymentContext = React.useCallback(async () => {
    const active = booking || savedReservation;
    if (!active?.reservationToken) return;
    setPayment((current) => ({ ...current, submitting: true, error: null }));
    try {
      await refreshReservation(active, { resume: true });
      setPayment((current) => ({ ...current, submitting: false }));
    } catch (error) {
      setPayment((current) => ({
        ...current,
        submitting: false,
        error: mapBookingError(error, t),
      }));
    }
  }, [booking, refreshReservation, savedReservation, t]);

  const paymentContextRecoveryRef = React.useRef(new Set());
  React.useEffect(() => {
    if (
      step !== 4 ||
      payment.state !== 'ready' ||
      !payment.bookingId ||
      !payment.reservationToken ||
      (payment.paymentProvider === 'omise' && payment.publicKey && payment.supportedMethods.length > 0)
    ) {
      return;
    }
    const recoveryKey = `${payment.bookingId}:${payment.reservationToken}`;
    if (paymentContextRecoveryRef.current.has(recoveryKey)) return;
    paymentContextRecoveryRef.current.add(recoveryKey);
    reloadPaymentContext();
  }, [
    payment.bookingId,
    payment.reservationToken,
    payment.state,
    payment.paymentProvider,
    payment.publicKey,
    payment.supportedMethods,
    reloadPaymentContext,
    step,
  ]);

  const openPolicy = React.useCallback(() => setDialog('refund'), []);
  const openContact = React.useCallback(() => setDialog('contact'), []);
  const closeDialog = React.useCallback(() => setDialog(null), []);

  const shellProps = {
    shop: null,
    locale,
    onChangeLocale: changeLocale,
    onOpenPolicy: openPolicy,
    onOpenContact: openContact,
    dialogKind: dialog,
    dialogSlug: slug,
    onCloseDialog: closeDialog,
    embedded,
  };

  // ── RENDER ─────────────────────────────────────────────────────────────
  if (ui.loading) {
    return (
      <Shell {...shellProps}>
        <Card>
          <div className="sp-message">{t('common.loading')}</div>
        </Card>
      </Shell>
    );
  }
  if (ui.error) {
    return (
      <Shell {...shellProps}>
        <Card>
          <div className="sp-message is-error">{ui.error}</div>
        </Card>
      </Shell>
    );
  }
  if (!ui.shop || !ui.shop.name) {
    return (
      <Shell {...shellProps}>
        <Card>
          <div className="sp-message is-error">{t('common.error')}</div>
        </Card>
      </Shell>
    );
  }

  const currentService = ui.services.find((s) => s.id === draft.serviceId) || null;
  const extrasIndex = indexExtras(ui.services);
  const summaryLines = buildSummaryLines({
    service: currentService,
    extrasQuantities: draft.extrasQuantities,
    extrasIndex,
    locale,
  });
  const totalPrice = currentService
    ? currentService.price + Object.entries(draft.extrasQuantities).reduce((sum, [eid, qty]) => {
        const extra = extrasIndex[eid];
        return sum + (extra ? extra.price * qty : 0);
      }, 0)
    : 0;
  const extras = currentService ? currentService.extras || [] : [];
  // An empty assignedStaffIds list means the service can be performed by
  // everyone. "Best available" keeps staffId null, so the backend assigns
  // the least-booked eligible person for the selected time.
  const assignedStaff = currentService
    ? (currentService.assignedStaffIds || []).map(String)
    : [];
  const eligibleStaff = currentService
    ? ui.staff.filter(
        (member) =>
          assignedStaff.length === 0 || assignedStaff.includes(String(member.id))
      )
    : [];
  const showStaff = eligibleStaff.length > 0;
  const selectedScheduleKey = scheduleKey(draft.serviceId, draft.staffId);
  const isCurrentScheduleLoaded = loadedScheduleKey === selectedScheduleKey;

  return (
    <Shell
      shop={ui.shop}
      locale={locale}
      onChangeLocale={changeLocale}
      onOpenPolicy={openPolicy}
      onOpenContact={openContact}
      dialogKind={dialog}
      dialogSlug={slug}
      onCloseDialog={closeDialog}
      embedded={embedded}
    >
      {step < 5 && <Stepper step={step} locale={locale} />}

      {step === 1 && (
        <>
          {savedReservation && (
            <>
              <SavedReservationCard
                reservation={savedReservation}
                locale={locale}
                errorMessage={payment.error}
                onOpen={async () => {
                  const inferredStatus = savedReservation.status ||
                    (savedReservation.bookingCode ? 'confirmed' : 'pending');
                  if (inferredStatus === 'confirmed') {
                    setBooking({ ...savedReservation, status: 'confirmed' });
                    setStep(5);
                    if (savedReservation.reservationToken) {
                      refreshReservation(savedReservation).catch(() => {});
                    }
                    return;
                  }
                  if (inferredStatus === 'cancelled' || isExpiredReservation(savedReservation)) {
                    clearSavedReservation(slug);
                    setSavedReservation(null);
                    setBooking(null);
                    idempotencyKeyRef.current = crypto.randomUUID();
                    setStep(2);
                    return;
                  }
                  try {
                    await refreshReservation(savedReservation, {
                      resume: inferredStatus === 'pending',
                    });
                  } catch (error) {
                    setPayment((current) => ({
                      ...current,
                      error: mapBookingError(error, t),
                    }));
                  }
                }}
                onExpire={expireCurrentReservation}
              />
              <div style={{ height: 12 }} />
            </>
          )}
          <StepService
            services={ui.services}
            categories={ui.categories}
            category={draft.categoryFilter ?? null}
            onCategoryChange={(categoryFilter) =>
              setDraft((d) => ({ ...d, categoryFilter }))
            }
            onSelect={(serviceId) => {
              setDraft((d) => ({
                ...d,
                serviceId,
                staffId: null,
                slot: null,
                slotDate: null,
              }));
              // Step 2 = time, which embeds the eligible-staff picker when
              // the shop has staff. So we always go to step 2 here.
              setStep(2);
            }}
            locale={locale}
          />
        </>
      )}

      {step === 2 && (
        <>
          {showStaff && (
            <>
              <StepStaff
                staff={eligibleStaff}
                selectedId={draft.staffId}
                onSelect={(staffId) =>
                  setDraft((d) => ({ ...d, staffId, slot: null }))
                }
                locale={locale}
              />
              <div style={{ height: 12 }} />
            </>
          )}
          <StepSlot
            // Never briefly display the previous therapist's calendar while
            // a therapist-specific availability request is in flight.
            days={isCurrentScheduleLoaded ? ui.schedule.days : []}
            loading={scheduleLoading || !isCurrentScheduleLoaded}
            selectedDate={draft.slotDate}
            selectedSlot={draft.slot}
            onSelectDate={(slotDate) =>
              setDraft((d) => ({ ...d, slotDate, slot: null }))
            }
            onSelectSlot={(slot) => setDraft((d) => ({ ...d, slot }))}
            locale={locale}
          />
          {extras.length > 0 && (
            <>
              <div style={{ height: 12 }} />
              <StepExtras
                extras={extras}
                quantities={draft.extrasQuantities}
                onChange={(extraId, qty) =>
                  setDraft((d) => ({
                    ...d,
                    extrasQuantities: {
                      ...d.extrasQuantities,
                      [extraId]: qty,
                    },
                  }))
                }
                locale={locale}
              />
            </>
          )}
          {currentService && <PriceSummary lines={summaryLines} locale={locale} />}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 12 }}>
            <Button variant="secondary" onClick={() => setStep(1)} icon={<ArrowLeft size={14} />}>
              {t('common.back')}
            </Button>
            <Button
              disabled={!draft.slot || !draft.slotDate}
              onClick={() => setStep(3)}
            >
              {t('common.next')}
            </Button>
          </div>
        </>
      )}

      {step === 3 && currentService && draft.slot && (
        <>
          <StepDetails
            initial={booking?.customer ?? null}
            submitting={payment.submitting}
            errorMessage={payment.error}
            locale={locale}
            onBack={() => setStep(2)}
            onSubmit={async (details) => {
              setPayment((p) => ({ ...p, submitting: true, error: null }));
              try {
                const created = await api.createBooking(slug, {
                  serviceId: draft.serviceId,
                  staffId: draft.staffId ?? null,
                  slotDate: draft.slotDate,
                  slotStart: draft.slot.start,
                  slotEnd: draft.slot.end,
                  customerName: details.customerName,
                  customerEmail: details.customerEmail,
                  customerPhone: details.customerPhone,
                  notes: details.notes,
                  locale,
                  extras: Object.entries(draft.extrasQuantities)
                    .filter(([, q]) => q > 0)
                    .map(([extraId, quantity]) => ({ extraId, quantity })),
                  idempotencyKey: idempotencyKeyRef.current,
                });
                const pendingBooking = {
                  ...details,
                  id: created.bookingId,
                  bookingId: created.bookingId,
                  reservationToken: created.reservationToken,
                  status: created.status || 'pending',
                  paymentStatus: created.paymentStatus || 'requires_payment',
                  paymentProvider: created.paymentProvider,
                  totalPrice: created.totalPrice,
                  currency: created.currency,
                  expiresAt: created.expiresAt ?? null,
                  slotDate: draft.slotDate,
                  slotStart: draft.slot.start,
                  slotEnd: draft.slot.end,
                  serviceName: pickLocalized(currentService.translations, locale),
                  summary: summaryLines,
                };
                setBooking(pendingBooking);
                storeReservation(pendingBooking);
                setPayment({
                  bookingId: created.bookingId,
                  reservationToken: created.reservationToken,
                  paymentProvider: created.paymentProvider,
                  publicKey: created.publicKey,
                  supportedMethods: created.supportedMethods || [],
                  nextAction: created.nextAction || null,
                  expiresAt: created.expiresAt ?? null,
                  totalPrice: created.totalPrice,
                  currency: created.currency,
                  state: created.paymentStatus === 'processing' ? 'processing' : 'ready',
                  submitting: false,
                  error: null,
                });
                if (created.status === 'confirmed') {
                  await refreshReservation(pendingBooking);
                } else {
                  setStep(4);
                }
              } catch (err) {
                const message = mapBookingError(err, t);
                if (err instanceof APIError && err.message === 'SLOT_TAKEN') {
                  setPayment((current) => ({ ...current, submitting: false, error: message }));
                  setStep(2);
                } else {
                  if (err instanceof APIError && err.message === 'PAYMENT_SETUP_FAILED') {
                    idempotencyKeyRef.current = crypto.randomUUID();
                  }
                  setPayment((current) => ({ ...current, submitting: false, error: message }));
                }
              }
            }}
          />
        </>
      )}

      {step === 4 && (
        ['confirming', 'processing'].includes(payment.state) && payment.nextAction?.type !== 'qr' ? (
          <PaymentStatusCard state={payment.state} locale={locale} />
        ) : payment.state === 'expired' ? (
          <Card title={t('pay.heading')}>
            <div className="sp-message is-error">{t('pay.expiredMessage')}</div>
            <div style={{ marginTop: 12, display: 'flex', justifyContent: 'flex-end' }}>
              <Button onClick={cancelAndChooseAnother}>{t('reservation.chooseAnother')}</Button>
            </div>
          </Card>
        ) : embedded ? (
          <CheckoutHandoffCard
            reservation={booking || savedReservation}
            slug={slug}
            locale={locale}
          />
        ) : (
          <StepOmisePay
            publicKey={payment.publicKey}
            supportedMethods={payment.supportedMethods}
            nextAction={payment.nextAction}
            totalPrice={payment.totalPrice ?? booking?.totalPrice ?? totalPrice}
            currency={payment.currency ?? booking?.currency ?? 'THB'}
            expiresAt={payment.expiresAt}
            submitting={payment.submitting}
            errorMessage={payment.error}
            onSubmitNonce={submitOmiseNonce}
            onExpired={expireCurrentReservation}
            onBack={cancelAndChooseAnother}
            locale={locale}
          />
        )
      )}

      {step === 5 && booking && ['confirmed', 'completed'].includes(booking.status) && (
        <StepDone
          booking={booking}
          service={
            (currentService && pickLocalized(currentService.translations, locale)) ||
            booking?.serviceName
          }
          locale={locale}
          // Pass the client-computed total as a fallback. The server
          // returns the authoritative total in the POST /booking
          // response (which becomes booking.totalPrice), but if for
          // any reason it's 0 or missing — e.g. the server fell back
          // to a stale booking row, or a future server-side edit
          // hasn't propagated — we'd rather show the amount the user
          // agreed to pay than render "฿0".
          fallbackTotal={totalPrice}
        />
      )}
    </Shell>
  );
}

function Shell({ shop, locale, onChangeLocale, onOpenPolicy, onOpenContact, dialogKind, onCloseDialog, dialogSlug, embedded, children }) {
  return (
    <div className={`sp-app${embedded ? ' sp-app--embedded' : ''}`}>
      <div className="sp-app__header">
        <div className="sp-app__brand">
          <div className="sp-app__brand-avatar">
            {shop?.avatarUrl ? (
              <img src={shop.avatarUrl} alt={shop.name} />
            ) : (
              (shop?.name || '?').slice(0, 1).toUpperCase()
            )}
          </div>
          <div className="sp-app__brand-text">
            <div className="sp-app__brand-name">{shop?.name || 'Book an appointment'}</div>
            <div className="sp-app__brand-meta">
              {[shop?.location, shop?.businessType].filter(Boolean).join(' · ') || 'Thailand'}
            </div>
          </div>
        </div>
        <LocaleSwitch locale={locale} onChange={onChangeLocale} />
      </div>
      {children}
      <Footer
        locale={locale}
        onOpenPolicy={onOpenPolicy}
        onOpenContact={onOpenContact}
      />
      {dialogKind && (
        <PolicyDialog
          kind={dialogKind}
          slug={dialogSlug}
          locale={locale}
          shop={shop}
          onClose={onCloseDialog}
        />
      )}
    </div>
  );
}

function CheckoutHandoffCard({ reservation, slug, locale }) {
  const t = tFor(locale);
  const checkoutUrl = React.useMemo(() => {
    if (!reservation?.id || !reservation?.reservationToken) return null;
    try {
      return buildCheckoutHandoffUrl({
        origin: window.location.origin,
        slug,
        bookingId: reservation.id,
        reservationToken: reservation.reservationToken,
      });
    } catch {
      return null;
    }
  }, [reservation?.id, reservation?.reservationToken, slug]);

  React.useEffect(() => {
    if (!checkoutUrl || window.parent === window) return;
    window.parent.postMessage({
      type: CHECKOUT_MESSAGE_TYPE,
      url: checkoutUrl,
    }, '*');
  }, [checkoutUrl]);

  if (!checkoutUrl) {
    return (
      <Card title={t('pay.heading')}>
        <div className="sp-message is-error">{t('common.error')}</div>
      </Card>
    );
  }

  return (
    <Card title={t('pay.secureHandoff')}>
      <div className="sp-handoff" role="status" aria-live="polite">
        <div className="sp-handoff__pulse" aria-hidden="true" />
        <p>{t('pay.secureHandoffBody')}</p>
        <a className="sp-handoff__link" href={checkoutUrl} target="_top">
          {t('pay.secureHandoffButton')}
        </a>
      </div>
    </Card>
  );
}

function SavedReservationCard({ reservation, locale, errorMessage, onOpen, onExpire }) {
  const t = tFor(locale);
  const status = reservation.status || (reservation.bookingCode ? 'confirmed' : 'pending');
  const processing = reservation.paymentStatus === 'processing';
  const expired = status === 'cancelled' || (!processing && isExpiredReservation(reservation));
  const message = status === 'confirmed'
    ? t('reservation.summary').replace('{code}', reservation.bookingCode)
    : expired
      ? t('reservation.expired')
      : processing
        ? t('reservation.processing')
        : t('reservation.pending');
  const action = status === 'confirmed'
    ? t('reservation.view')
    : expired
      ? t('reservation.chooseAnother')
      : processing
        ? t('reservation.processing')
        : t('reservation.completePayment');

  return (
    <Card title={t('reservation.heading')}>
      <div style={{ color: 'var(--text-muted)', marginBottom: 12 }}>{message}</div>
      {errorMessage && <div className="sp-message is-error" style={{ marginBottom: 12 }}>{errorMessage}</div>}
      {!expired && status === 'pending' && !processing && reservation.expiresAt && (
        <div style={{ marginBottom: 12 }}>
          <CountdownTimer expiresAt={reservation.expiresAt} locale={locale} onExpire={onExpire} />
        </div>
      )}
      <Button variant="secondary" onClick={onOpen} disabled={processing}>
        {action}
      </Button>
    </Card>
  );
}

function PaymentStatusCard({ state, locale }) {
  const t = tFor(locale);
  return (
    <Card title={t('pay.heading')}>
      <div className="sp-message" role="status" aria-live="polite">
        {state === 'confirming' ? t('pay.confirming') : t('pay.processingMessage')}
      </div>
    </Card>
  );
}

function isExpiredReservation(reservation) {
  if (!reservation?.expiresAt) return false;
  const expiresAt = Date.parse(reservation.expiresAt);
  return Number.isFinite(expiresAt) && expiresAt <= Date.now();
}

function mapBookingError(error, t) {
  if (!(error instanceof APIError)) return error?.message || t('common.error');
  if (error.message === 'MERCHANT_ONLINE_PAYMENTS_NOT_READY') return t('error.merchantPayments');
  if (error.message === 'PAYMENTS_TEMPORARILY_UNAVAILABLE') return t('error.paymentsUnavailable');
  if (error.message === 'PAYMENT_PROVIDER_UNAVAILABLE') return t('error.paymentsUnavailable');
  if (error.message === 'SLOT_TAKEN') return t('error.slotTaken');
  if (error.message === 'BOOKING_EXPIRED') return t('pay.expiredMessage');
  if (error.message === 'PAYMENT_SETUP_FAILED') return t('error.paymentsUnavailable');
  return t('common.error');
}

function readSlugFromUrl() {
  const path = window.location.pathname || '/';
  const trimmed = path.replace(/^\/+|\/+$/g, '');
  if (!trimmed) return 'demo';
  return trimmed.split('/')[0];
}

// A response is usable only for the same service/therapist pair that
// requested it. This prevents a previous "best available" response from
// briefly appearing after the customer picks an individual therapist.
function scheduleKey(serviceId, staffId) {
  return `${serviceId ?? ''}:${staffId ?? ''}`;
}

// localStorage key for the user's manual locale pick. Prefixed with
// `sp:` so we don't collide with anything else on the page, and keyed
// by slug so a tourist who bookmarks two merchants doesn't see one
// merchant's forced Thai layout on the other.
function localeStorageKey(slug) {
  return `sp:locale:${slug}`;
}

function readStoredLocale(slug) {
  try {
    const v = window.localStorage.getItem(localeStorageKey(slug));
    if (v === 'en' || v === 'th') return v;
  } catch {
    // localStorage may be disabled (Safari private mode, etc.) —
    // detection still works in-memory.
  }
  return null;
}

function writeStoredLocale(slug, locale) {
  try {
    window.localStorage.setItem(localeStorageKey(slug), locale);
  } catch {
    // ignore
  }
}

function collectCategories(services) {
  const byId = new Map();
  for (const s of services) {
    if (s.category) {
      byId.set(String(s.category.id), s.category);
    }
  }
  return Array.from(byId.values());
}

function indexExtras(services) {
  const out = {};
  for (const s of services) {
    for (const e of s.extras || []) {
      out[String(e.id)] = e;
    }
  }
  return out;
}
